import React, { useState } from 'react';
import { useAuth } from 'wasp/client/auth';
import { useAction } from 'wasp/client/operations';
import { useQuery } from 'wasp/client/operations';
import { useTier } from '../hooks/useTier';
import { UpgradePrompt } from './UpgradePrompt';
import { getUserHouseholds } from 'wasp/client/operations';
import { createHousehold } from 'wasp/client/operations';
import { inviteHouseholdMember } from 'wasp/client/operations';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Users, Home, Mail, Plus } from 'lucide-react';
import { toast } from 'sonner';

export function HouseholdManagement() {
  const { householdLimit, requiresUpgrade } = useTier();
  const { data: userHouseholds } = useQuery(getUserHouseholds, {});

  // Assume current household is the first one or selected
  const currentHousehold = userHouseholds?.[0];
  const currentMembers = currentHousehold?.members?.length || 0;

  if (currentMembers >= householdLimit) {
    return <UpgradePrompt requiredTier="starter" featureName="Add more household members (max 6 in Starter)" />;
  }

  const { data: user } = useAuth();
  const [selectedHousehold, setSelectedHousehold] = useState<any>(null);
  const [invitingEmail, setInvitingEmail] = useState('');
  const [newHouseholdName, setNewHouseholdName] = useState('');

  const doCreateHousehold = useAction(createHousehold);
  const doInviteMember = useAction(inviteHouseholdMember);

  const handleCreateHousehold = async () => {
    if (!newHouseholdName.trim()) {
      toast.error('Household name is required');
      return;
    }

    try {
      const result = await doCreateHousehold({
        name: newHouseholdName,
      });

      if ((result as any).success) {
        toast.success('Household created successfully!');
        setNewHouseholdName('');
      }
    } catch (error) {
      toast.error('Failed to create household');
    }
  };

  const handleInviteMember = async () => {
    if (!selectedHousehold || !invitingEmail.trim()) {
      toast.error('Please select a household and enter an email');
      return;
    }

    if (selectedHousehold.members.length >= householdLimit) {
      toast.error('Upgrade to add more members');
      return;
    }

    try {
      const result = await doInviteMember({
        householdId: selectedHousehold.id,
        email: invitingEmail,
      });

      if ((result as any).success) {
        toast.success('Invitation sent successfully!');
        setInvitingEmail('');
      }
    } catch (error) {
      toast.error('Failed to send invitation');
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Home className="h-5 w-5" />
            Household Management
          </CardTitle>
        </CardHeader>
        <CardContent className="p-6">
          {userHouseholds && userHouseholds.length === 0 ? (
            <div className="space-y-4">
              <p className="text-muted-foreground">You haven't created a household yet.</p>
              <div className="space-y-2">
                <Label htmlFor="household-name">Household Name</Label>
                <Input
                  id="household-name"
                  value={newHouseholdName}
                  onChange={(e) => setNewHouseholdName(e.target.value)}
                  placeholder="Enter household name"
                />
                <Button onClick={handleCreateHousehold} className="w-full">
                  <Plus className="mr-2 h-4 w-4" />
                  Create Household
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {userHouseholds.map((household: any) => (
                  <Card key={household.id} className="p-4">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        {household.name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {household.members.length} members
                      </p>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-2">
                        {household.members.map((member: any) => (
                          <div key={member.userId} className="flex items-center justify-between">
                            <span className="text-sm">
                              {member.user.firstName || member.user.email}
                            </span>
                            <Badge variant="outline">
                              {member.role}
                            </Badge>
                          </div>
                        ))}
                        <div className="pt-2">
                          <Label htmlFor="invite-email">Invite Member Email</Label>
                          <div className="flex gap-2">
                            <Input
                              id="invite-email"
                              value={invitingEmail}
                              onChange={(e) => setInvitingEmail(e.target.value)}
                              placeholder="Enter email to invite"
                            />
                            <Button onClick={() => handleInviteMember()} size="sm">
                              <Mail className="h-4 w-4 mr-1" />
                              Invite
                            </Button>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
